import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import axios from 'axios';
import { toError, assertOk } from '@/services/normalize';
import type { ExtAxiosConfig } from '@/middleware/axios-ui';

import {
  BASE_URL,
  GET_GAMES,
  GET_GETGAMES,
  GET_TRANSACTIONS,
  EDIT_TRANSACTION,
  GAME_MAINTENANCE,
  GAME_VERSION,
  MAINTENANCE_ALL_SOCKET,
  MAINTENANCE_GAMES_SOCKET,
  EDIT_DEMO_URL,
  GET_GAMES_RTP_LIST,
  UPDATE_RTP,
  M_POST,
  M_GET,
  GET_RISK_MANAGEMENT_LIST,
  UPDATE_CONFIGS_RISK,
  EDIT_VERSION_MASCOT,
  UPDATE_AGENT_RTP
} from '@/utils/api';

@Module({ name: 'game' })
export default class Game extends VuexModule {
  game: any = [];

  @Mutation
  mutateGame(game: any) {
    this.game = game;
  }

  // ---------- Games ----------
  @Action({ commit: 'mutateGame', rawError: true })
  async FetchGame(payload: any) {
    try {
      const { partner, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + GET_GAMES,
        data: {
          partner: partner?.partner
        },
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch game failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch game failed');
    }
  }

  @Action({ commit: 'mutateGame', rawError: true })
  async FetchGetGame() {
    try {
      const { data } = await axios({
        url: BASE_URL + GET_GETGAMES,
        method: M_GET
      });

      assertOk(data, 'Fetch game list failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch game list failed');
    }
  }

  // ---------- Maintenance / Version / Demo URL ----------
  @Action({ rawError: true })
  async GameMaintenance(status: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + GAME_MAINTENANCE,
        data: status,
        method: M_POST
      });

      assertOk(data, 'Game maintenance failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Game maintenance failed');
    }
  }

  @Action({ rawError: true })
  async GameVersion(payload: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + GAME_VERSION,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Game version failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Game version failed');
    }
  }

  @Action({ rawError: true })
  async EditDemoUrl(payload: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + EDIT_DEMO_URL,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Edit demo url failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Edit demo url failed');
    }
  }

  @Action({ rawError: true })
  async MaintenanceAllSocket(isMaintenance: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + MAINTENANCE_ALL_SOCKET,
        data: isMaintenance,
        method: M_POST
      });

      assertOk(data, 'Maintenance all socket failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Maintenance all socket failed');
    }
  }

  @Action({ rawError: true })
  async MaintenanceGamesSocket(isMaintenance: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + MAINTENANCE_GAMES_SOCKET,
        data: isMaintenance,
        method: M_POST
      });

      assertOk(data, 'Maintenance games socket failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Maintenance games socket failed');
    }
  }

  // ---------- Version Control ----------
  @Action({ rawError: true })
  async FetchVersionControl() {
    try {
      const { data } = await axios({
        url: BASE_URL + GET_TRANSACTIONS,
        method: M_POST
      });

      assertOk(data, 'Fetch version control failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch version control failed');
    }
  }

  @Action({ rawError: true })
  async EditVersion(payload: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + EDIT_TRANSACTION,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Edit version failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Edit version failed');
    }
  }

  @Action({ rawError: true })
  async EditVersionMascot(payload: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + EDIT_VERSION_MASCOT,
        data: payload,
        method: M_POST
      });

      assertOk(data, 'Edit version mascot failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Edit version mascot failed');
    }
  }

  // ---------- RTP ----------
  @Action({ rawError: true })
  async FetchGameRtpList(payload?: any) {
    try {
      const { __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + GET_GAMES_RTP_LIST,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch game rtp list failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch game rtp list failed');
    }
  }

  @Action({ rawError: true })
  async EditUpdateRtp(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_RTP,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update rtp failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Update rtp failed');
    }
  }

  @Action({ rawError: true })
  async UpdateAgentRTP(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_AGENT_RTP,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update agent rtp failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Update agent rtp failed');
    }
  }

  // ---------- Risk Management ----------
  @Action({ rawError: true })
  async FetchRiskManagementList(payload: any) {
    try {
      const { criteria, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + GET_RISK_MANAGEMENT_LIST,
        method: M_POST,
        data: criteria,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch risk management list failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch risk management list failed');
    }
  }

  @Action({ rawError: true })
  async UpdateRiskManagementConfigs(params: Record<string, any>) {
    try {
      const { data } = await axios({
        url: BASE_URL + UPDATE_CONFIGS_RISK,
        data: params,
        method: M_POST
      });

      assertOk(data, 'Update risk management configs failed');
      return data?.status;
    } catch (err: any) {
      throw toError(err, 'Update risk management configs failed');
    }
  }
}
